import { MessageCircle } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { AnecdoteTooltip } from "../components/AnecdoteTooltip";
import { QuestionStatusBadge } from "../components/Badges/QuestionStatusBadge";
import { AddPicturesButton } from "../components/Buttons/AddPicturesButton";
import { ChapterNavButton } from "../components/Buttons/ChapterNavButton";
import { QuestionTableActionsButton } from "../components/Buttons/QuestionTableActionsButton";
import { AddPicturesModal } from "../components/Modals/AddPicturesModal";
import { ProgressBar } from "../components/ProgressBar";
import { AnecdotesModalContext } from "../contexts/AnecdotesModalContext";
import { BookContext } from "../contexts/BookContext";
import { Locale } from "../types/models/Language";

export const Chapter = () => {
  const { t } = useTranslation();
  const local = localStorage.getItem("i18nextLng") as Locale;
  const bookContext = useContext(BookContext);
  const { openModal, isOpen: isAnecdotesModalOpen } = useContext(AnecdotesModalContext);
  const { chapterNum } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddImagesModalOpen, setIsAddImagesModalOpen] = useState(false);

  const chapter =
    chapterNum === undefined
      ? undefined
      : bookContext.chapters.find(
          (chapter) => chapter.number === parseInt(chapterNum)
        );

  const chapterCount = bookContext.chapters.length;

  const questions = bookContext.questions.filter(
    (question) => question.chapter_id === chapter?.id
  );

  const questions_ids = questions.map((question) => question.id);

  const answers = bookContext.answers.filter((answer) =>
    questions_ids.includes(answer.question_id)
  );

  const completedAnswers = answers.filter((answer) => answer.completed);

  const getQuestionAnswer = (questionId: number) => {
    return answers.find((answer) => answer.question_id === questionId);
  };

  const handleOpenAnecdotesModal = () => {
    if (!chapter) return;

    // Find the generated chapter corresponding to this chapter (if it exists)
    const generatedChapter = bookContext.generated_chapters.find(
      (gc) => gc.chapter_id === chapter.id
    );

    // Open modal with chapter.number (always present) and generatedChapter.id (optional)
    openModal(
      chapter.number,
      (local && chapter?.content[local]) || "",
      generatedChapter?.id
    );
  };

  // Open anecdotes modal automatically if testimonials=true in query string
  useEffect(() => {
    if (searchParams.get("testimonials") === "true" && chapter) {
      handleOpenAnecdotesModal();
    }
  }, [searchParams, chapter]);

  // Remove testimonials parameter when modal is closed
  useEffect(() => {
    if (!isAnecdotesModalOpen && searchParams.get("testimonials") === "true") {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("testimonials");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [isAnecdotesModalOpen, searchParams, setSearchParams]);

  if (!chapter || chapterNum === undefined) return null;

  return (
    <>
      <div className="px-3 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-8 flex flex-1">
        <div className="bg-white flex flex-1 flex-col justify-between shadow-md rounded">
          <div>
            <div className="flex flex-col sm:flex-row p-3 sm:p-4">
              <div className="flex flex-1 flex-col">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-700 mb-2 lg:mb-0">
                    {local && chapter?.content[local]}
                  </h1>
                  <div className="flex flex-col sm:flex-row items-start lg:items-end gap-2">
                    <AnecdoteTooltip position="bottom">
                      <button
                        onClick={handleOpenAnecdotesModal}
                        className="flex items-center gap-2 bg-herb-500 rounded hover:bg-herb-600 transition-colors gap-x-1 sm:gap-x-2  px-2 sm:px-3.5 py-1.5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-herb-600"
                      >
                        <MessageCircle size={18} />
                        <span className="font-nunito text-sm">
                          {t("anecdotes.modal.requestFriend")}
                        </span>
                      </button>
                    </AnecdoteTooltip>
                    <AddPicturesButton
                      onClick={() => setIsAddImagesModalOpen(true)}
                    />
                  </div>
                </div>
                <p className="font-nunito text-slate-50 text-sm sm:text-base mb-4">
                  {chapter?.description}
                </p>
                <div className="flex flex-col gap-y-2 sm:gap-x-6">
                  <p className="font-nunito font-extrabold text-slate-700 text-sm sm:text-base">
                    {t("chapter.questions")}: {completedAnswers.length} /
                    {questions.length}
                  </p>
                  <ProgressBar
                    percentage={
                      (completedAnswers.length / questions.length) * 100
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-row w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-2 sm:py-3.5 pl-2 sm:pl-6 pr-1 sm:pr-3 text-left text-xs sm:text-sm font-nunito font-semibold text-slate-50"
                    >
                      {t("chapter.question").toUpperCase()}
                    </th>
                    <th
                      scope="col"
                      className="px-1 sm:px-3 py-2 sm:py-3.5 text-left text-xs sm:text-sm font-nunito font-semibold text-slate-50"
                    >
                      {t("chapter.status").toUpperCase()}
                    </th>
                    <th
                      scope="col"
                      className="relative py-2 sm:py-3.5 pl-1 sm:pl-3 pr-2 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white shadow-md">
                  {questions
                    .sort((a, b) => a.number - b.number)
                    .map((question) => (
                      <tr key={question.id}>
                        <td className="py-3 sm:py-4 pl-2 sm:pl-6 pr-1 sm:pr-3 text-xs sm:text-sm font-nunito font-normal text-slate-700">
                          <div className="max-w-xs sm:max-w-none">
                            {question.content[local]}
                          </div>
                        </td>
                        <td className="px-1 sm:px-3 py-3 sm:py-4 text-xs sm:text-sm text-slate-500">
                          <QuestionStatusBadge
                            answer={getQuestionAnswer(question.id)}
                          />
                        </td>
                        <td className="relative py-3 sm:py-4 pl-1 sm:pl-3 pr-2 sm:pr-6 text-right text-xs sm:text-sm font-medium">
                          <QuestionTableActionsButton
                            question={question}
                            answer={getQuestionAnswer(question.id)}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex sm:flex-row justify-between px-3 sm:px-4 py-3 mt-4 gap-3 sm:gap-0">
            <div>
              {parseInt(chapterNum) != 1 && (
                <ChapterNavButton mode="prev" chapterNum={chapterNum} />
              )}
            </div>
            <div>
              {parseInt(chapterNum) != chapterCount && (
                <ChapterNavButton mode="next" chapterNum={chapterNum} />
              )}
            </div>
          </div>
        </div>
      </div>
      <AddPicturesModal
        open={isAddImagesModalOpen}
        setOpen={setIsAddImagesModalOpen}
        chapter_id={chapter.id}
      />
    </>
  );
};
